import { faArrowLeft, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';

const SliderContainer = styled.div`
  display: flex;
  overflow: hidden;
  width: 100%;
  height: 100%;
  aspect-ratio: 16 / 10;
  position: relative;
  margin-right: 20px;
  margin-top: 0px;
`;

const Slide = styled.div`
  min-width: 100%;
  transition: transform 0.5s ease-in-out;
`;

const DownloadIconContainer = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  cursor: pointer;
  z-index: 3;
  opacity: 0;
  transition: opacity 0.3s;
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;

  &:hover ${DownloadIconContainer} {
    opacity: 1;
  }
`;

const Image = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: cover;
  z-index: 2;
`;

const BlurContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;
  overflow: hidden;

  img {
    filter: blur(20px);
    transform: scale(1.3);
    position: absolute;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
  }
`;

const ArrowButton = styled.button`
  background: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 3;

  ${({ direction }) => (direction === 'left' ? 'left: 10px;' : 'right: 10px;')}
`;

const ImageSlider = ({ images, onChange = () => {} }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (images.length) {
      onChange(currentIndex);
    }
  }, [currentIndex, onChange, images.length]);

  const handlePrev = useCallback(() => {
    setCurrentIndex(prevIndex => (prevIndex === 0 ? images.length - 1 : prevIndex - 1));
  }, [images.length]);

  const handleNext = useCallback(() => {
    setCurrentIndex(prevIndex => (prevIndex === images.length - 1 ? 0 : prevIndex + 1));
  }, [images.length]);

  return (
    <SliderContainer>
      {images.length > 1 && (
        <ArrowButton direction="left" onClick={handlePrev}>
          <FontAwesomeIcon icon={faArrowLeft} />
        </ArrowButton>
      )}
      {images.map((image, index) => (
        <Slide key={index} style={{ transform: `translateX(-${currentIndex * 100}%)` }}>
          <ImageWrapper>
            {image.src && (
              <>
                <BlurContainer>
                  <img src={image.src} alt={`Blur background ${index + 1}`} />
                </BlurContainer>
                <Image src={image.src} alt={`Slide ${index + 1}`} />
              </>
            )}
          </ImageWrapper>
        </Slide>
      ))}
      {images.length > 1 && (
        <ArrowButton direction="right" onClick={handleNext}>
          <FontAwesomeIcon icon={faArrowRight} />
        </ArrowButton>
      )}
    </SliderContainer>
  );
};

ImageSlider.propTypes = {
  images: PropTypes.arrayOf(
    PropTypes.shape({
      src: PropTypes.string.isRequired,
      height: PropTypes.string
    })
  ).isRequired,
  onChange: PropTypes.func
};

export default ImageSlider;
